<template>
  <div class="container mx-auto p-0 sm:p-0 lg:p-0">
    <!-- Header Section -->
    <header class="flex justify-between items-center py-6 border-b border-gray-200">
      <div class="text-2xl font-bold gradient-text">
        {{ portfolio.name }}
      </div>
      <nav class="hidden md:flex items-center space-x-6">
        <a v-for="link in portfolio.navLinks" :key="link.name" :href="link.url" class="text-gray-600 hover:text-indigo-600 transition-colors duration-300">{{ link.name }}</a>
      </nav>
      <a :href="'mailto:' + portfolio.email" class="hidden sm:inline-block bg-indigo-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors duration-300">
        Contact Me
      </a>
    </header>

    <main class="mt-12">
      <!-- Hero Section -->
      <section id="hero" class="text-center py-16">
        <img :src="portfolio.avatar" alt="My Avatar" class="w-32 h-32 rounded-full mx-auto mb-6 border-4 border-white shadow-lg">
        <h1 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Hi, I'm <span class="gradient-text">{{ portfolio.name }}</span>
        </h1>
        <p class="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">{{ portfolio.tagline }}</p>
        <div class="flex justify-center space-x-5">
          <a v-for="social in portfolio.socials" :key="social.name" :href="social.url" target="_blank" class="text-gray-500 hover:text-indigo-600 transition-colors duration-300">
            <i :data-lucide="social.icon" class="w-6 h-6"></i>
            <span class="sr-only">{{ social.name }}</span>
          </a>
        </div>
      </section>

      <!-- About Me Section -->
      <section id="about" class="py-16">
        <div class="max-w-4xl mx-auto bg-white p-8 rounded-xl shadow-md">
          <h2 class="text-3xl font-bold text-center mb-6 text-gray-900">About Me</h2>
          <p class="text-gray-700 leading-relaxed text-center whitespace-pre-line">{{ portfolio.bio }}</p>
        </div>
      </section>

      <!-- Skills Section -->
      <section id="skills" class="py-16 bg-gray-50 rounded-xl">
        <div class="max-w-4xl mx-auto px-4">
          <h2 class="text-3xl font-bold text-center mb-10 text-gray-900">My Skills</h2>
          <div class="flex flex-wrap justify-center gap-4">
            <span v-for="skill in portfolio.skills" :key="skill" class="bg-indigo-100 text-indigo-700 font-medium py-2 px-4 rounded-full text-sm">
              {{ skill }}
            </span>
          </div>
        </div>
      </section>

      <!-- Projects Section -->
      <section id="projects" class="py-16">
        <h2 class="text-3xl font-bold text-center mb-10 text-gray-900">Featured Projects</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="project in portfolio.projects" :key="project.title" class="bg-white rounded-xl overflow-hidden shadow-md card-hover-effect">
            <img :src="project.imageUrl" :alt="project.title" class="w-full h-48 object-cover" onerror="this.onerror=null;this.src='https://placehold.co/600x400/e2e8f0/4a5568?text=Project+Image';">
            <div class="p-6">
              <h3 class="text-xl font-bold mb-2 text-gray-900">{{ project.title }}</h3>
              <p class="text-gray-700 mb-4">{{ project.description }}</p>
              <div class="flex flex-wrap gap-2 mb-4">
                <span v-for="tag in project.tags" :key="tag" class="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded-full">{{ tag }}</span>
              </div>
              <div class="flex items-center justify-end space-x-4 mt-4">
                <a :href="project.liveUrl" target="_blank" class="text-indigo-600 hover:underline font-medium flex items-center">
                  Live Demo <i data-lucide="external-link" class="w-4 h-4 ml-1"></i>
                </a>
                <a :href="project.repoUrl" target="_blank" class="text-gray-500 hover:text-gray-800">
                  <i data-lucide="github" class="w-5 h-5"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Footer Section -->
    <footer class="text-center py-8 border-t border-gray-200 mt-12">
      <p class="text-gray-600">&copy; {{ new Date().getFullYear() }} {{ portfolio.name }}. All rights reserved.</p>
    </footer>
  </div>
</template>

<script setup>
import { onMounted, reactive ,nextTick} from 'vue';
// Corrected import: Use a named import for createIcons
import { createIcons,icons } from 'lucide';

// --- PORTFOLIO DATA ---
// Data is now a reactive object.
const portfolio = reactive({
  name: "Konsta Laurell",
  email: "Konstalaurell@gmail.com",
  avatar: "https://placehold.co/256x256/c7d2fe/4338ca?text=KL",
  tagline: "A passionate Backend Developer creating modern and responsive web applications.",
bio: `I'm a backend developer with a strong focus on building scalable, secure, and high-performance server-side applications. I specialize in designing APIs, working with databases, and developing robust backend systems using modern technologies.

I enjoy solving complex problems, automating workflows, and optimizing infrastructure. When I'm not coding, you’ll find me exploring new tech stacks, gaming, or experimenting with server setups.
I use arch, btw`,

  navLinks: [
    { name: "About", url: "#about" },
    { name: "Skills", url: "#skills" },
    { name: "Projects", url: "#projects" },
  ],
  socials: [
    { name: "GitHub", url: "https://github.com/KonstaLaurell", icon: "github" },
    { name: "LinkedIn", url: "https://linkedin.com", icon: "linkedin" },
  ],
  skills: [
    "HTML5", "CSS3", "JavaScript (ES6+)", "Vue.js", "React",
    "Node.js", "Express", "Figma", "Git", "Webpack", "Python",
    "MongoDB","MySQL","JWT","Bcrypt.js","CORS","Body-parser", "Socket.io", 
    "Mui","React Native", "React Native Calendars", "React Native Biometric", "AsyncStorage", "Expo Secure Store", "Expo Router",
    "Arch Linux", "Ubuntu Server",
  ],
  projects: [
    {
      title: "LiveNotepad",
      description: "A full-featured online note-taking app with real-time collaboration.",
      imageUrl: "https://placehold.co/600x400/a5b4fc/1e293b?text=LiveNotepad",
      tags: ["Express", "JWT", "Bcrypt.js", "Node.js","CORS","Body-parser", "MongoDB","Mongoose", "Socket.io","React.js","Mui"],
      liveUrl: "#",
      repoUrl: "https://github.com/KonstaLaurell/Livenotepad_Public",
    },
        {
      title: "Webchat",
      description: "This is a real-time chat application built using Node.js, Express, and Socket.io. It includes features like user authentication, message history, spam prevention, banning/unbanning users, and more.",
      imageUrl: "https://placehold.co/600x400/a5b4fc/1e293b?text=Webchat",
      tags: ["Express", "JWT", "Bcrypt.js", "Node.js","CORS","Body-parser", "MongoDB","Mongoose", "Socket.io","React.js"],
      liveUrl: "#",
      repoUrl: "https://github.com/KonstaLaurell/webchat_Public",
    },
            {
      title: "Diary App",
      description: "This is a mobile diary application built with React Native. It allows users to create, edit, and delete diary entries, with features like biometric authentication for security.",
      imageUrl: "https://placehold.co/600x400/a5b4fc/1e293b?text=Diary+App",
      tags: ["React Native","React Native Calendars","React Native Biometric","AsyncStorage","Expo Secure Store","Expo Router"],
      liveUrl: "#",
      repoUrl: "https://github.com/KonstaLaurell/Diary",
    },
    
  ]
});

// This hook is called after the component is mounted.
// We use it to initialize the Lucide icons.
onMounted(async() => {
  // Call the imported function directly
  await nextTick();
  createIcons({icons});
    

});
</script>

<style>
@import "tailwindcss";
/* These styles are global but could be scoped to the component by adding the 'scoped' attribute */
body {
  
    font-family: 'Inter', sans-serif;
    background-color: #f8fafc; /* A very light gray background */
    color: #1f2937; /* Default text color */
}

.gradient-text {
    background: linear-gradient(to right, #4f46e5, #ec4899);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}

.card-hover-effect {
    transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
}

.card-hover-effect:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}
</style>
